import React, { forwardRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { getChoices, getRankingOptions } from "../../../services/commons";
import AnswerNps from "../AnswerNps";
import AnswerScale5 from "../AnswerScale5";
import AnswerScale9 from "../AnswerScale9";
import AnswerContact from "../AnswerContact";
import AnswerSingleSelect from "../AnswerSingleSelect";
import AnswerRangeNumeric from "../AnswerRangeNumeric";
import AnswerMultiSelect from "../AnswerMultiSelect";
import AnswerSatisfactionThumbs from "../AnswerSatisfactionThumbs";
import AnswerSatisfactionFaces from "../AnswerSatisfactionFaces";
import AnswerShort from "../AnswerShort";
import AnswerLong from "../AnswerLong";
import AnswerContent from "../AnswerContent";
import AnswerMediaUpload from "../AnswerMediaUpload";
import AnswerMatrix from "../AnswerMatrix";
import AnswerRanking from "../AnswerRanking";
import HiddenQuestion from "../HiddenQuestion";
import AnswerDate from "../AnswerDate";
import AnswerDropdown from "../AnswerDropdown";
import AnswerScale10 from "../AnswerScale10";
import AnswerNumber from "../AnswerNumber";
import AnswerImageSelect from "../AnswerImageSelect";
import {
  longAnswerMaxCount,
  shortAnswerMaxCount,
} from "../../../services/constants";
import { OTHER, NONE } from "../../constants";

export function convertArrayToObj(arr) {
  const obj = {};
  arr.forEach((item) => {
    obj[item.index] = { column: item.answer, row: item.question };
  });
  return obj;
}

const VALUE_PREFIX = "Value_";

export function setValueChoiceQuestionType(value) {
  if (value === OTHER) {
    return OTHER;
  } else if (value === NONE) {
    return NONE;
  } else {
    return value.replace(VALUE_PREFIX, "");
  }
}

const RenderQuestion = (
  { q, answers, surveyId, setAnswers, currentLang, questions },
  ref
) => {
  const { currentTheme, data } = useSelector((state) => state.survey);
  const survey = data?.[surveyId] || data;
  const previewing = new URLSearchParams(window.location.search).get(
    "previewing"
  );
  const setAnswer = useCallback(
    (q, a) => {
      let updateOthers = false;
      if (q.type === "textarea") {
        if (a.length > longAnswerMaxCount) {
          a = a.substring(0, longAnswerMaxCount);
        }
      } else if (q.type === "textfield") {
        if (a.length > shortAnswerMaxCount) {
          a = a.substring(0, shortAnswerMaxCount);
        }
      } else if (q.type === "radios" || q.type === "checkboxes") {
        if (q.other === true && q.otherQuestionId) {
          if (
            (q.multiple === true && a && a.indexOf(OTHER) === -1) ||
            (q.multiple === false && a === OTHER)
          ) {
            updateOthers = true;
          }
        }
      }

      if (
        a &&
        (q.type === "radios" || q.type === "checkboxes" || q.type === "select")
      ) {
        if (q.multiple === true) {
          for (const i in a) {
            a[i] = setValueChoiceQuestionType(a[i]);
          }
        } else {
          a = setValueChoiceQuestionType(a);
        }
      }

      if (a && typeof a === "string" && a.length > 0 && a.trim().length === 0) {
        a = "";
      }

      if (a && q.type === "checkboxes" && a.length === 0) {
        a = null;
      }

      if (!updateOthers) {
        setAnswers((prevAnswers) => {
          return {
            ...prevAnswers,
            [q.id]: a,
          };
        });
      } else {
        setAnswers((prevAnswers) => {
          return {
            ...prevAnswers,
            [q.id]: a,
            [q.otherQuestionId]: "",
          };
        });
      }
    },
    [setAnswers]
  );

  const handleOtherOptionSelection = useCallback(
    (q, answer) => {
      const hiddenQuestions = survey.hiddenQuestions;
      const index = hiddenQuestions.findIndex(
        (hiddenQue) => hiddenQue.id === q.otherQuestionId
      );
      //in case of a piped question, add prefix with answer to consolidate in api/index.js
      if (q.isPipedFrom) {
        const loopedOtherQuestion = {
          id: q.otherQuestionId,
          question: "Other, if so please tell us more",
          help: "",
          required: false,
          type: "textarea",
          hidden: true,
          isDescriptionEnabled: false,
          pipingParentID: q.pipingParentID,
          questionID: q.id,
        };
        setAnswer(loopedOtherQuestion, answer);
      } else if (index !== -1) {
        setAnswer(hiddenQuestions[index], answer);
      }
    },
    [survey, setAnswer]
  );
  if (!q.formattingType || (q.formattingType && previewing)) {
    switch (q.type) {
      case "textfield":
        return (
          <AnswerShort
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            helperText={q.help}
            handleChange={(a) => setAnswer(q, a)}
            value={answers[q.id] ? answers[q.id] : ""}
            formattingType={q.formattingType || null}
          />
        );
      case "contact_form":
        return (
          <AnswerContact
            ref={ref}
            key={q.id}
            currentTheme={currentTheme}
            type={q.type}
            question={q.question}
            required={q.required}
            helperText={q.help}
            handleChange={(a) => setAnswer(q, a)}
            value={answers[q.id] ? answers[q.id] : []}
            contactForms={q.contactForms}
            currentLang={currentLang}
          />
        );
      case "textarea":
        return (
          <AnswerLong
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            helperText={q.help}
            handleChange={(a) => setAnswer(q, a)}
            value={answers[q.id] ? answers[q.id] : ""}
            maxChar={longAnswerMaxCount}
          />
        );
      case "hidden":
        return (
          <HiddenQuestion
            key={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            jsonKey={q.jsonKey}
            handleChange={(a) => setAnswer(q, a)}
            value={answers[q.id]}
            helperText={q.help}
            currentLang={currentLang}
          />
        );
      case "radios":
        return (
          <AnswerSingleSelect
            key={q.id}
            type="radios"
            questionId={q.id}
            currentTheme={currentTheme}
            oldOtherLabel={q.oldOtherLabel}
            question={q.question}
            required={q.required}
            choicesType={q.choicesType}
            handleChange={(a) => setAnswer(q, a)}
            choices={getChoices(survey, q, answers, questions)}
            helperText={q.help}
            handleOtherOptionSelection={(a) => handleOtherOptionSelection(q, a)}
            other={q.other}
            otherValue={
              q.other === true
                ? answers[q.otherQuestionId]
                  ? answers[q.otherQuestionId]
                  : ""
                : ""
            }
            random={q.random}
            asc={q.asc}
            value={answers[q.id] || ""}
            currentLang={currentLang}
            formattingType={q.formattingType || null}
          />
        );
      case "checkboxes":
        return (
          <AnswerMultiSelect
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            oldOtherLabel={q.oldOtherLabel}
            question={q.question}
            required={q.required}
            choicesType={q.choicesType}
            handleChange={(a) => setAnswer(q, a)}
            choices={getChoices(survey, q, answers, questions)}
            helperText={q.help}
            handleOtherOptionSelection={(a) => handleOtherOptionSelection(q, a)}
            other={q.other}
            otherValue={
              q.other === true
                ? answers[q.otherQuestionId]
                  ? answers[q.otherQuestionId]
                  : ""
                : ""
            }
            none={q.none}
            random={q.random}
            asc={q.asc}
            value={answers[q.id] || []}
            currentLang={currentLang}
          />
        );
      case "select":
        return (
          <AnswerDropdown
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            handleChange={(a) => setAnswer(q, a)}
            choices={getChoices(survey, q, answers, questions)}
            helperText={q.help}
            value={answers[q.id] || ""}
            currentLang={currentLang}
            formattingType={q.formattingType || null}
          />
        );
      case "range":
        if (!q.max && q.preDefinedOption && q.optionsList) {
          return (
            <AnswerSingleSelect
              key={q.id}
              type="range"
              questionId={q.id}
              currentTheme={currentTheme}
              question={q.question}
              required={q.required}
              choicesType={"static"}
              handleChange={(a) => setAnswer(q, a)}
              choices={getChoices(survey, q, answers, questions)}
              helperText={q.help}
              random={false}
              asc={false}
              value={answers[q.id] || ""}
            />
          );
        }
        switch (q.max) {
          case 5:
            return (
              <AnswerScale5
                key={q.id}
                currentTheme={currentTheme}
                question={q.question}
                required={q.required}
                handleChange={(a) => setAnswer(q, a)}
                low={q.optionsList?.[0] || q.endpointLeft}
                middle={""}
                high={
                  q.optionsList?.[q.optionsList.length - 1] || q.endpointRight
                }
                helperText={q.help}
                value={answers[q.id] || ""}
              />
            );
          case 9:
            return (
              <AnswerScale9
                key={q.id}
                currentTheme={currentTheme}
                question={q.question}
                required={q.required}
                handleChange={(a) => setAnswer(q, a)}
                low={q.endpointLeft}
                middle={""}
                high={q.endpointRight}
                helperText={q.help}
                value={answers[q.id] || ""}
              />
            );
          case 11: //answer scale 10 question takes value 11 since 10 is being used for NPS
            return (
              <AnswerScale10
                key={q.id}
                currentTheme={currentTheme}
                question={q.question}
                required={q.required}
                handleChange={(a) => setAnswer(q, a)}
                low={q.optionsList?.[0] || q.endpointLeft}
                middle={""}
                high={
                  q.optionsList?.[q.optionsList.length - 1] || q.endpointRight
                }
                helperText={q.help}
                value={answers[q.id] || ""}
              />
            );
          case 10:
            return (
              <AnswerNps
                key={q.id}
                npsTitle={q.npsTitle}
                questionId={q.id}
                currentTheme={currentTheme}
                subject={q.question}
                required={q.required}
                handleChange={(a) => setAnswer(q, a)}
                helperText={q.help}
                recommendTo={q.recommendTo}
                value={answers[q.id] || ""}
                currentLang={currentLang}
              />
            );
          default:
            return <></>;
        }
      case "webform_rating":
        switch (q.max) {
          case 2:
            return (
              <AnswerSatisfactionThumbs
                key={q.id}
                questionId={q.id}
                currentTheme={currentTheme}
                question={q.question}
                handleChange={(a) => setAnswer(q, a)}
                required={q.required}
                helperText={q.help}
                value={answers[q.id] || 0}
              />
            );
          default:
            return <></>;
        }
      case "rangeNumeric":
        return (
          <AnswerRangeNumeric
            key={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            handleChange={(a) => setAnswer(q, a)}
            helperText={q.help}
            value={answers[q.id] || ""}
            scale={q.scale}
            low={q.endpointLeft}
            high={q.endpointRight}
          />
        );
      case "faces":
        switch (q.max) {
          case 6:
            return (
              <AnswerSatisfactionFaces
                key={q.id}
                questionId={q.id}
                currentTheme={currentTheme}
                question={q.question}
                handleChange={(a) => setAnswer(q, a)}
                required={q.required}
                helperText={q.help}
                value={answers[q.id] ? answers[q.id] - 1 : null}
                currentLang={currentLang}
              />
            );
          default:
            return <></>;
        }
      case "matrix":
        return (
          <AnswerMatrix
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            helperText={q.help}
            row={q.matrixRow}
            column={q.matrixColumn}
            handleChange={(a) => setAnswer(q, a)}
            value={
              answers[q.id] && answers[q.id].length
                ? convertArrayToObj(answers[q.id])
                : {}
            }
          />
        );
      case "ranking":
        return (
          <AnswerRanking
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            currentLang={currentLang}
            question={q.question}
            required={q.required}
            helperText={q.help}
            rankingOptions={getRankingOptions(q, answers, questions)}
            handleChange={(a) => setAnswer(q, a)}
            endpoint={q.endpoint}
            low={q.endpointLeft}
            high={q.endpointRight}
            value={answers[q.id] && answers[q.id].length ? answers[q.id] : []}
          />
        );
      case "content":
        return (
          <AnswerContent
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            helperText={q.help}
            videoPath={q.videoPath}
            videoLink={q.videoLink}
            imagePath={q.imagePath}
          />
        );
      case "mediaUpload":
        return (
          <AnswerMediaUpload
            key={q.id}
            questionLabel={q.questionLabel}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            type={q.type}
            value={answers[q.id] || {}}
            helperText={q.help}
            maxFile={q.maxFile}
            fileSizeType={q.fileSizeType}
            checkedFileTypes={q.checkedFileTypes}
            handleChange={(a) => setAnswer(q, a)}
          />
        );
      case "number":
        return (
          <AnswerNumber
            key={q.id}
            questionId={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            helperText={q.help}
            interactionType={q.interactionType}
            min={parseFloat(q.range.min)}
            max={parseFloat(q.range.max)}
            stepSize={q.stepSize ? parseFloat(q.stepSize) : 1}
            labels={q.labels}
            handleChange={(a) => setAnswer(q, a)}
            value={answers[q.id] || answers[q.id] === 0 ? answers[q.id] : ""}
            currentLang={currentLang}
          />
        );
      case "datetime":
        return (
          <AnswerDate
            key={q.id}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            format={q.format}
            customSelection={q.customSelection}
            handleChange={(a) => setAnswer(q, a)}
            choices={getChoices(survey, q, answers, questions)}
            helperText={q.help}
            value={answers[q.id] || ""}
            currentLang={currentLang}
          />
        );
      case "image":
        return (
          <AnswerImageSelect
            key={q.id}
            surveyId={surveyId}
            currentTheme={currentTheme}
            question={q.question}
            required={q.required}
            helperText={q.help}
            choices={q.choices}
            isMultipleAnswers={q.isMultipleAnswers}
            handleChange={(a) => setAnswer(q, a)}
            value={answers[q.id] || []}
            currentLang={currentLang}
          />
        );
      default:
        return <></>;
    }
  }
};

export default forwardRef(RenderQuestion);
